import {
  AUTOMATION_LEVELS,
  MCP_TRANSPORTS,
  METHOD_STATUSES,
  ROLE_KINDS,
  TAILORING_DECISIONS,
  type AutomationLevel,
  type McpTransport,
  type MethodStatus,
  type RoleKind,
  type TailoringDecisionValue,
} from '@dpem/ontology';
import * as z from 'zod';

/**
 * Enumerations. Anything that is an ontology term is imported from
 * `@dpem/ontology` and merely wrapped in a zod enum for transport — the
 * ontology stays the only place a method vocabulary value is declared.
 */

/** Platform RBAC roles (PLATFORM-BRIEF §2). Not an ontology `Role` — that is a method actor. */
export const ACTOR_ROLES = [
  'ADMIN',
  'METHOD_AUTHOR',
  'REVIEWER',
  'WORKSHOP_FACILITATOR',
  'VIEWER',
] as const;
export const ActorRoleSchema = z.enum(ACTOR_ROLES);
export type ActorRole = z.infer<typeof ActorRoleSchema>;

/** Every authenticated caller holds at least one role; VIEWER is the floor. */
export const AUTHENTICATED_ROLES = ACTOR_ROLES;

/**
 * Account lifecycle, mirroring the API's `user_status` enum. On the wire only for
 * the administrator's own account list — nothing else needs to know, and no
 * authentication response distinguishes these states, on purpose: telling a caller
 * that an address exists but is suspended turns the login form into a directory.
 *
 * There is no DELETED member because the platform has no delete: audit provenance
 * points at accounts with `onDelete: Restrict`, so an account is SUSPENDED forever
 * rather than removed.
 */
export const USER_ACCOUNT_STATUSES = ['INVITED', 'ACTIVE', 'SUSPENDED'] as const;
export const UserAccountStatusSchema = z.enum(USER_ACCOUNT_STATUSES);
export type UserAccountStatus = z.infer<typeof UserAccountStatusSchema>;

/** MethodVersion lifecycle — ontology `METHOD_STATUSES`. */
export const MethodStateSchema = z.enum(METHOD_STATUSES);
export type MethodState = MethodStatus;
export { METHOD_STATUSES };

/** Tailoring verdict per element — ontology `TAILORING_DECISIONS`. */
export const DecisionKindSchema = z.enum(TAILORING_DECISIONS);
export type DecisionKind = TailoringDecisionValue;
export { TAILORING_DECISIONS };

export const AutomationLevelSchema = z.enum(AUTOMATION_LEVELS);
export type { AutomationLevel };
export { AUTOMATION_LEVELS };

export const RoleKindSchema = z.enum(ROLE_KINDS);
export type { RoleKind };
export { ROLE_KINDS };

export const McpTransportSchema = z.enum(MCP_TRANSPORTS);
export type { McpTransport };
export { MCP_TRANSPORTS };

/** Workshop session lifecycle (architecture §3). */
export const WORKSHOP_STATES = [
  'DRAFT',
  'IN_PROGRESS',
  'COMPLETED',
  'PUBLISHED',
  'ABANDONED',
] as const;
export const WorkshopStateSchema = z.enum(WORKSHOP_STATES);
export type WorkshopState = z.infer<typeof WorkshopStateSchema>;

/** Review verdicts on an `IN_REVIEW` method version. */
export const REVIEW_VERDICTS = ['APPROVE', 'REJECT'] as const;
export const ReviewVerdictSchema = z.enum(REVIEW_VERDICTS);
export type ReviewVerdict = z.infer<typeof ReviewVerdictSchema>;

/**
 * Authorable graph element kinds. These are the ontology node types the
 * authoring canvas can create directly; containment and reference targets
 * still come from the ontology.
 */
export const ELEMENT_KINDS = [
  'phase',
  'stage',
  'workPractice',
  'activity',
  'role',
  'gate',
  'lens',
  'ceremony',
] as const;
export const ElementKindSchema = z.enum(ELEMENT_KINDS);
export type ElementKind = z.infer<typeof ElementKindSchema>;

/**
 * What a workshop's binding override does to one activity → target pair.
 *
 * Three values because there are three answers a room can give about a tool, and the
 * third is not "do nothing". `ADD` binds a target the baseline does not use (the client
 * runs Azure DevOps), `REMOVE` drops one it does (they do not run Jira), and `CLEAR`
 * deletes the override itself — the room changed its mind and wants the baseline back.
 * Without `CLEAR` an override could only ever be replaced by its opposite, which is not
 * the same statement: `REMOVE` on a target the baseline never bound is a decision the
 * minutes would then carry forever for no reason.
 */
export const BINDING_OVERRIDE_ACTIONS = ['ADD', 'REMOVE', 'CLEAR'] as const;
export const BindingOverrideActionSchema = z.enum(BINDING_OVERRIDE_ACTIONS);
export type BindingOverrideAction = z.infer<typeof BindingOverrideActionSchema>;

/**
 * What a method parameter holds. Deliberately small.
 *
 * A parameter is a value a client supplies once and the tailored method then reads —
 * "sprint length", "issue tracker", "regulated?" — so the set is the set a facilitator
 * can type into a form and a plan generator can act on. `TEXT` is the fallback and
 * carries no promise beyond being a string; `NUMBER` and `BOOLEAN` are parsed, so a plan
 * can multiply or branch on them; `DURATION` is an ISO-8601 period, which is what a work
 * breakdown needs to add to a date; `ENUM` is a choice from `allowedValues`.
 *
 * No `DATE`: a project start date is an input to a *plan*, not a property of a method,
 * and putting it here would invite two answers to the same question.
 */
export const PARAMETER_VALUE_TYPES = ['TEXT', 'NUMBER', 'BOOLEAN', 'DURATION', 'ENUM'] as const;
export const ParameterValueTypeSchema = z.enum(PARAMETER_VALUE_TYPES);
export type ParameterValueType = z.infer<typeof ParameterValueTypeSchema>;

/** Typed dependency edges between activities (PLATFORM-BRIEF §5). */
export const DEPENDENCY_KINDS = ['precedes', 'dependsOn', 'loopsTo'] as const;
export const DependencyKindSchema = z.enum(DEPENDENCY_KINDS);
export type DependencyKind = z.infer<typeof DependencyKindSchema>;

/** Catalog / deliverable things an activity can be bound to. */
export const BINDING_KINDS = [
  'deliverable',
  'tool',
  'asset',
  'agent',
  'agentSkill',
  'mcpServer',
] as const;
export const BindingKindSchema = z.enum(BINDING_KINDS);
export type BindingKind = z.infer<typeof BindingKindSchema>;

/** Catalog aggregate roots (architecture §2). */
export const CATALOG_KINDS = ['tool', 'asset', 'agent', 'agentSkill', 'mcpServer'] as const;
export const CatalogKindSchema = z.enum(CATALOG_KINDS);
export type CatalogKind = z.infer<typeof CatalogKindSchema>;

/**
 * Where a proposal to move a session entry into the platform catalogue stands.
 *
 * Three states and no fourth: a proposal is open, or somebody with `catalog:manage`
 * answered it. There is deliberately no WITHDRAWN — a facilitator who changes their mind
 * asks the author to decline, because the answer to "who decided this" must always name
 * the person who holds the platform catalogue.
 */
export const PROMOTION_STATES = ['PENDING', 'ACCEPTED', 'DECLINED'] as const;
export const PromotionStateSchema = z.enum(PROMOTION_STATES);
export type PromotionState = z.infer<typeof PromotionStateSchema>;

/** Things an attachment or external link can hang off. */
export const ATTACHMENT_OWNER_TYPES = [
  'tool',
  'asset',
  'agent',
  'agentSkill',
  'mcpServer',
  'activity',
  'deliverable',
] as const;
export const AttachmentOwnerTypeSchema = z.enum(ATTACHMENT_OWNER_TYPES);
export type AttachmentOwnerType = z.infer<typeof AttachmentOwnerTypeSchema>;
